// Elevation profile between two nodes: shortest pipe path over the drawn
// network (Dijkstra, weighted by pipe length) and the elevation series
// (ground + invert, if calculated) along that path.

function getProp(element, key) {
  const prop =
    element.props?.find((p) => p.key === key) ||
    element.props?.find((p) => p.label === key);
  return prop && prop.value !== undefined ? prop.value : null;
}

function getCalc(element, key) {
  const prop = element.calc?.find((p) => p.key === key);
  return prop && prop.value !== undefined ? prop.value : null;
}

function edgeLength(edge) {
  const length = Number(getProp(edge, "length"));
  return Number.isFinite(length) && length > 0 ? length : 1;
}

// shortest path (by pipe length) between two node ids.
// Returns { nodes: [nodeElement...], edges: [edgeElement...] } or null when the
// nodes are not connected. Linear-scan min is fine for network sizes here.
export function findShortestPath(mapElements, startId, endId) {
  const adjacency = new Map();
  mapElements.forEach((el) => {
    if (el.type !== "edge") return;
    if (!adjacency.has(el.startNodeId)) adjacency.set(el.startNodeId, []);
    if (!adjacency.has(el.endNodeId)) adjacency.set(el.endNodeId, []);
    const length = edgeLength(el);
    adjacency.get(el.startNodeId).push({ node: el.endNodeId, edge: el, length });
    adjacency.get(el.endNodeId).push({ node: el.startNodeId, edge: el, length });
  });

  const dist = new Map([[startId, 0]]);
  const previous = new Map(); // nodeId -> { node: predecessorId, edge }
  const visited = new Set();
  const queue = new Set([startId]);

  while (queue.size > 0) {
    let current = null;
    queue.forEach((id) => {
      if (current === null || dist.get(id) < dist.get(current)) current = id;
    });
    queue.delete(current);
    if (current === endId) break;
    visited.add(current);

    (adjacency.get(current) || []).forEach(({ node, edge, length }) => {
      if (visited.has(node)) return;
      const alt = dist.get(current) + length;
      if (alt < (dist.has(node) ? dist.get(node) : Infinity)) {
        dist.set(node, alt);
        previous.set(node, { node: current, edge });
        queue.add(node);
      }
    });
  }

  if (startId !== endId && !previous.has(endId)) return null;

  const nodeIds = [endId];
  const edges = [];
  let cursor = endId;
  while (cursor !== startId) {
    const step = previous.get(cursor);
    if (!step) return null;
    edges.unshift(step.edge);
    nodeIds.unshift(step.node);
    cursor = step.node;
  }

  const nodeById = new Map(
    mapElements.filter((el) => el.type === "node").map((el) => [el.id, el])
  );
  const nodes = nodeIds.map((id) => nodeById.get(id));
  if (nodes.some((node) => !node)) return null;
  return { nodes, edges };
}

// Profile series along a path. Ground elevation comes from the node props,
// invert elevation from the optimization result (calc); missing inverts stay
// null so the chart shows a gap instead of interpolating.
export function buildProfileData(path) {
  const ground = [];
  const invert = [];
  const pumps = [];
  const nodeX = [];
  let distance = 0;
  let hasInvert = false;

  path.nodes.forEach((node, i) => {
    if (i > 0) distance += edgeLength(path.edges[i - 1]);
    const x = Math.round(distance * 100) / 100;
    nodeX.push(x);
    const name = node.name;

    const g = getProp(node, "elevation");
    ground.push({ x, y: g != null ? Number(g) : null, name });

    // pump station (lift station): mark it so the profile can flag it with a
    // symbol at the node's position
    const isLift = getProp(node, "lift_station");
    if (isLift) {
      pumps.push({ x, name, ground: g != null ? Number(g) : null });
    }

    const inv = getCalc(node, "invert_elevation");
    const invOut = getCalc(node, "invert_elevation_outflow");
    const inEdge = i > 0 ? path.edges[i - 1] : null;
    const outEdge = i < path.nodes.length - 1 ? path.edges[i] : null;

    // A lift station exposes two invert levels (inflow / outflow); which one
    // faces the incoming vs the outgoing profile segment is defined by the pipe
    // flow orientation (node === edge.startNodeId -> outflow side), NOT by the
    // direction the cross-section was drawn. Every other node uses its single
    // invert on both sides.
    const isLiftStep =
      isLift &&
      inv != null &&
      invOut != null &&
      Number(invOut) !== Number(inv);
    const faceInvert = (edge) =>
      isLiftStep && edge && edge.startNodeId === node.id
        ? Number(invOut)
        : inv != null
          ? Number(inv)
          : null;
    const baseIn = faceInvert(inEdge || outEdge);
    const baseOut = faceInvert(outEdge || inEdge);

    // outlet_offset: a pipe discharging into a DEEPER manhole ends above that
    // manhole's invert by this amount (a drop / "Absturz"). It belongs to the
    // pipe's downstream end (edge.endNodeId), so it is added to whichever face
    // the arriving pipe connects to — independent of the drawing direction.
    const arrivalOffset = (edge) => {
      if (!edge || edge.endNodeId !== node.id) return 0;
      const off = Number(getCalc(edge, "outlet_offset"));
      return Number.isFinite(off) ? off : 0;
    };
    const offIn = arrivalOffset(inEdge);
    const offOut = arrivalOffset(outEdge);

    // tiny x offsets keep the extra points distinct (for the hover slices) and
    // draw the vertical drops. Node spacing (pipe length) is always well above
    // these, so ordering across nodes is preserved.
    if (baseIn == null) {
      invert.push({ x, y: null, name });
    } else {
      // incoming pipe arrives raised by its drop, then falls to the manhole bottom
      if (offIn) invert.push({ x: x - 0.01, y: baseIn + offIn, name });
      invert.push({ x, y: baseIn, name });
      // lift-station pump lift between the inflow and outflow invert
      if (baseOut != null && baseOut !== baseIn) {
        invert.push({ x: x + 0.01, y: baseOut, name });
      }
      // a pipe that actually ARRIVES here via the outgoing segment (profile
      // drawn against the flow) leaves the manhole at its raised arrival level
      if (offOut) {
        const outBase = baseOut != null ? baseOut : baseIn;
        invert.push({ x: x + 0.02, y: outBase + offOut, name });
      }
      hasInvert = true;
    }
  });

  // pressure pipes: edges flagged as pressure_pipe -> the distance range they
  // span along the profile, so the chart can highlight those segments
  const pressureSegments = [];
  path.edges.forEach((edge, i) => {
    if (getProp(edge, "pressure_pipe")) {
      pressureSegments.push({ x1: nodeX[i], x2: nodeX[i + 1] });
    }
  });

  return {
    ground,
    invert: hasInvert ? invert : null,
    pumps,
    pressureSegments,
    totalLength: Math.round(distance * 100) / 100,
  };
}
